import React from 'react';
import * as ReactVirtualized from 'react-virtualized';

import { Box, PhotoIcon, makeStyles } from '@gogame/components';

import Image from '../components/Image';

const useStyles = makeStyles((theme) => ({
  root: {
    position: 'relative',
    width: ({ width }) => width,
    maxHeight: ({ height, heightCover, rootWidth, aspectRatio }) =>
      !isAuto(height)
        ? height
        : heightCover && rootWidth
          ? rootWidth / getRatio(aspectRatio)
          : null,
    overflow: 'hidden',
    alignSelf: 'center',
    background: ({ thumbnail }) =>
      thumbnail ? theme.palette.action.disabledBackground : null,

    '&:after': {
      position: 'absolute',
      display: ({ withOverlay }) => (withOverlay ? 'block' : 'none'),
      content: '""',
      bottom: 0,
      left: 0,
      right: 0,
      height: '30%',
      background: 'linear-gradient(rgba(0,0,0,0), rgba(0,0,0,0.48))',
    },
  },
  thumbnail: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: ({ height }) => (isAuto(height) ? 0 : height),
    paddingTop: ({ height, aspectRatio }) =>
      isAuto(height) ? getHeightByAspectRatio(aspectRatio) : null,
  },
  thumbnailIcon: {
    color: theme.palette.text.icon,
    marginBottom: ({ height }) => (isAuto(height) ? '50%' : null),
  },
  attributes: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 1,
    filter: ({ withOverlay }) =>
      withOverlay
        ? 'drop-shadow(0px 1px 1px rgba(0, 0, 0, 0.14)) drop-shadow(0px 2px 1px rgba(0, 0, 0, 0.12)) drop-shadow(0px 1px 3px rgba(0, 0, 0, 0.2))'
        : null,
  },
}));

const PreviewFile = ({
  src,
  thumbnail,
  width = 160,
  height = 90,
  heightCover,
  aspectRatio,
  attributes,
  withOverlay,
}) => {
  const [rootWidth, setRootWidth] = React.useState(null);

  const classes = useStyles({
    width,
    height,
    heightCover,
    aspectRatio,
    thumbnail,
    withOverlay,
    rootWidth: isNumberValue(width) ? Number(width) : rootWidth,
  });

  const handleResize = ({ width }) => {
    setRootWidth(width);
  };

  const withVirtualized = (children) =>
    heightCover && !isNumberValue(width) ? (
      <ReactVirtualized.AutoSizer disableHeight onResize={handleResize}>
        {({ width: rootWidth }) => <Box width={rootWidth}>{children}</Box>}
      </ReactVirtualized.AutoSizer>
    ) : (
      children
    );

  return (
    <div className={classes.root}>
      {withVirtualized(
        <React.Fragment>
          {thumbnail ? (
            <Box className={classes.thumbnail} width={width}>
              <PhotoIcon className={classes.thumbnailIcon} fontSize={'large'} />
            </Box>
          ) : (
            <Image
              src={src}
              width={width}
              height={heightCover ? 'auto' : height}
              aspectRatio={heightCover ? null : aspectRatio}
            />
          )}
          {attributes ? (
            <div className={classes.attributes}>{attributes}</div>
          ) : null}
        </React.Fragment>
      )}
    </div>
  );
};

export default PreviewFile;

function isAuto(value) {
  return `${value}` === 'auto';
}

function isNumberValue(value) {
  return !isNaN(Number(value));
}

function getRatio(aspectRatio) {
  const [horizontal, vertical] = aspectRatio.split('/');
  return horizontal / vertical;
}

function getHeightByAspectRatio(aspectRatio) {
  if (!aspectRatio) {
    return null;
  }
  return `${(getRatio(aspectRatio) * 100).toFixed(2)}%`;
}
